import React from 'react';
import { useNavigate } from 'react-router-dom';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PropertyCard = ({ property, onViewOffers, onGenerateReport, onEdit, onToggleActive }) => {
  const navigate = useNavigate();

  const formatDate = (date) => {
    return new Date(date)?.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const handleToggleActive = (e) => {
    e?.stopPropagation();
    onToggleActive?.(property?.id, !property?.isActive);
  };

  const handleReactivate = (e) => {
    e?.stopPropagation();
    onToggleActive?.(property?.id, true);
  };

  const handleCompareOffers = (e) => {
    e?.stopPropagation();
    onViewOffers(property?.id);
  };

  return (
    <div className={`bg-card border border-border rounded-lg overflow-hidden transition-smooth hover:shadow-elevation-md group ${!property?.isActive ? 'opacity-60' : ''}`}>
      <div 
        className="relative h-48 md:h-56 lg:h-64 overflow-hidden bg-muted cursor-pointer"
        onClick={() => onViewOffers(property?.id)}
      >
        <Image
          src={property?.imageUrl}
          alt={property?.imageAlt}
          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
        />
        {!property?.isActive && (
          <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
            <span className="bg-muted text-foreground px-4 py-2 rounded-lg font-semibold text-sm md:text-base">
              Not Active
            </span>
          </div>
        )}
      </div>
      <div className="p-3 md:p-4">
        <div className="flex items-start justify-between mb-2 md:mb-3">
          <div className="flex-1">
            <h3 className="text-base md:text-lg lg:text-xl font-semibold text-foreground mb-1.5 line-clamp-2">
              {property?.address}
            </h3>
            <div className="flex items-center gap-2 text-xs md:text-sm text-muted-foreground mb-1">
              <Icon name="User" size={14} className="md:w-4 md:h-4" />
              <span>{property?.owner}</span>
            </div>
            <div className="flex items-center gap-2 text-xs md:text-sm text-muted-foreground">
              <Icon name="Calendar" size={14} className="md:w-4 md:h-4" />
              <span>Added {formatDate(property?.dateAdded)}</span>
            </div>
          </div>
          <div className="flex flex-col items-end gap-2 ml-3">
            <button
              onClick={handleToggleActive}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${
                property?.isActive ? 'bg-primary' : 'bg-muted-foreground/30'
              }`}
              title={property?.isActive ? 'Mark as inactive' : 'Mark as active'}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  property?.isActive ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
            <span className="text-xs text-muted-foreground mb-1">
              {property?.isActive ? 'Active' : 'Inactive'}
            </span>
            <div className="flex items-center gap-2 border border-primary/30 rounded-md px-2 py-1 bg-primary/5">
              <Icon name="FileText" size={14} className="text-primary" />
              <span className="text-xs md:text-sm text-muted-foreground">
                {property?.offerCount} {property?.offerCount === 1 ? 'Offer' : 'Offers'}
              </span>
            </div>
          </div>
        </div>

        {property?.lastReportDate && (
          <div className="flex items-center gap-1.5 text-xs md:text-sm text-muted-foreground mb-3 pb-3 border-b border-border">
            <Icon name="Download" size={14} className="md:w-4 md:h-4" />
            <span className="hidden sm:inline">Last report: </span>
            <span>{formatDate(property?.lastReportDate)}</span>
          </div>
        )}

        {!property?.isActive ? (
          <div className="space-y-2 mt-3">
            <Button
              variant="default"
              size="md"
              fullWidth
              iconName="RotateCcw"
              iconPosition="left"
              onClick={handleReactivate}
              className="bg-success hover:bg-success/90"
            >
              Reactivate Property
            </Button>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                fullWidth
                iconName="Eye"
                iconPosition="left"
                onClick={() => onViewOffers(property?.id)}
                className="flex-1"
              >
                View Offers
              </Button>
              <Button
                variant="ghost"
                size="sm"
                iconName="Edit"
                onClick={() => onEdit(property?.id)}
                className="sm:w-auto"
              >
                <span className="sm:hidden">Edit</span>
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col sm:flex-row gap-2 mt-3">
            <Button
              variant="default"
              size="sm"
              fullWidth
              iconName="Eye"
              iconPosition="left"
              onClick={() => onViewOffers(property?.id)}
              className="flex-1"
            >
              Manage Offers
            </Button>
            {property?.offerCount > 0 && (
              <Button
                variant="outline"
                size="sm"
                fullWidth
                iconName="FileText"
                iconPosition="left"
                onClick={handleCompareOffers}
                className="flex-1"
              >
                Compare Offers
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              iconName="Edit"
              onClick={() => onEdit(property?.id)}
              className="sm:w-auto"
            >
              <span className="sm:hidden">Edit</span>
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PropertyCard;